import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, Plus, Edit, Trash2 } from "lucide-react";

export default function FMVMarketRates() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<any>(null);
  const [formData, setFormData] = useState({
    deal_type: "",
    athlete_tier: "",
    sport: "",
    market_type: "",
    institution_tier: "",
    min_amount: "",
    median_amount: "",
    max_amount: "",
    sample_size: "",
  });

  const { data: rates, isLoading } = useQuery({
    queryKey: ["fmv-market-rates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fmv_market_rates")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const { error } = await supabase
        .from("fmv_market_rates")
        .insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fmv-market-rates"] });
      toast.success("Market rate added successfully");
      setDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error("Failed to add market rate", {
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { error } = await supabase
        .from("fmv_market_rates")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fmv-market-rates"] });
      toast.success("Market rate updated successfully");
      setDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error("Failed to update market rate", {
        description: error.message,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("fmv_market_rates")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fmv-market-rates"] });
      toast.success("Market rate deleted successfully");
    },
    onError: (error: any) => {
      toast.error("Failed to delete market rate", {
        description: error.message,
      });
    },
  });

  const resetForm = () => {
    setFormData({
      deal_type: "",
      athlete_tier: "",
      sport: "",
      market_type: "",
      institution_tier: "",
      min_amount: "",
      median_amount: "",
      max_amount: "",
      sample_size: "",
    });
    setEditingRate(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const data = {
      deal_type: formData.deal_type,
      athlete_tier: formData.athlete_tier,
      sport: formData.sport,
      market_type: formData.market_type,
      institution_tier: formData.institution_tier,
      min_amount: parseFloat(formData.min_amount),
      median_amount: parseFloat(formData.median_amount),
      max_amount: parseFloat(formData.max_amount),
      sample_size: parseInt(formData.sample_size),
    };

    if (editingRate) {
      updateMutation.mutate({ id: editingRate.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (rate: any) => {
    setEditingRate(rate);
    setFormData({
      deal_type: rate.deal_type,
      athlete_tier: rate.athlete_tier,
      sport: rate.sport,
      market_type: rate.market_type,
      institution_tier: rate.institution_tier,
      min_amount: rate.min_amount.toString(),
      median_amount: rate.median_amount.toString(),
      max_amount: rate.max_amount.toString(),
      sample_size: rate.sample_size.toString(),
    });
    setDialogOpen(true);
  };

  const handleOpenDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">FMV Market Rates</h1>
            <p className="text-muted-foreground mt-2">
              Manage Fair Market Value benchmarks for NIL deals
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleOpenDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Add Market Rate
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingRate ? "Edit" : "Add"} Market Rate</DialogTitle>
                <DialogDescription>
                  Define FMV benchmarks for specific deal types and athlete tiers
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Deal Type *</Label>
                      <Select value={formData.deal_type} onValueChange={(v) => setFormData({ ...formData, deal_type: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="endorsement">Endorsement</SelectItem>
                          <SelectItem value="social_media">Social Media</SelectItem>
                          <SelectItem value="appearance">Appearance</SelectItem>
                          <SelectItem value="autograph">Autograph Session</SelectItem>
                          <SelectItem value="content_creation">Content Creation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Athlete Tier *</Label>
                      <Select value={formData.athlete_tier} onValueChange={(v) => setFormData({ ...formData, athlete_tier: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select tier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="elite">Elite (100k+ followers)</SelectItem>
                          <SelectItem value="high">High (50k-100k)</SelectItem>
                          <SelectItem value="mid">Mid (10k-50k)</SelectItem>
                          <SelectItem value="emerging">Emerging (&lt;10k)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Sport *</Label>
                      <Input
                        required
                        value={formData.sport}
                        onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                        placeholder="Football, Basketball, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Market Type *</Label>
                      <Select value={formData.market_type} onValueChange={(v) => setFormData({ ...formData, market_type: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select market" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="national">National</SelectItem>
                          <SelectItem value="regional">Regional</SelectItem>
                          <SelectItem value="local">Local</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Institution Tier *</Label>
                    <Select value={formData.institution_tier} onValueChange={(v) => setFormData({ ...formData, institution_tier: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select tier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="power_5">Power 5</SelectItem>
                        <SelectItem value="group_of_5">Group of 5</SelectItem>
                        <SelectItem value="fcs">FCS</SelectItem>
                        <SelectItem value="division_2">Division 2</SelectItem>
                        <SelectItem value="division_3">Division 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Min Amount ($) *</Label>
                      <Input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={formData.min_amount}
                        onChange={(e) => setFormData({ ...formData, min_amount: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Median Amount ($) *</Label>
                      <Input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={formData.median_amount}
                        onChange={(e) => setFormData({ ...formData, median_amount: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Amount ($) *</Label>
                      <Input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={formData.max_amount}
                        onChange={(e) => setFormData({ ...formData, max_amount: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Sample Size *</Label>
                    <Input
                      type="number"
                      required
                      min="0"
                      value={formData.sample_size}
                      onChange={(e) => setFormData({ ...formData, sample_size: e.target.value })}
                      placeholder="Number of deals in dataset"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                    {editingRate ? "Update" : "Create"} Rate
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Market Rate Database
            </CardTitle>
            <CardDescription>
              FMV benchmarks used for automated deal valuation
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Loading market rates...</div>
            ) : rates && rates.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Deal Type</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Sport</TableHead>
                    <TableHead>Market</TableHead>
                    <TableHead>Min-Med-Max</TableHead>
                    <TableHead>Sample Size</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.map((rate) => (
                    <TableRow key={rate.id}>
                      <TableCell className="font-medium capitalize">
                        {rate.deal_type.replace(/_/g, " ")}
                      </TableCell>
                      <TableCell className="capitalize">{rate.athlete_tier}</TableCell>
                      <TableCell>{rate.sport}</TableCell>
                      <TableCell className="capitalize">{rate.market_type}</TableCell>
                      <TableCell>
                        {formatCurrency(rate.min_amount)} - {formatCurrency(rate.median_amount)} - {formatCurrency(rate.max_amount)}
                      </TableCell>
                      <TableCell>{rate.sample_size} deals</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(rate)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteMutation.mutate(rate.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No market rates defined</p>
                <p className="text-sm mt-2">Add FMV benchmarks to enable dynamic deal valuation</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
